<template>
  <div class="container-fluid px-4 py-4" style="background:#f8fafc;min-height:100vh;">

    <div class="d-flex align-items-center justify-content-between flex-wrap mb-4" style="gap:12px;">
      <h4 class="text-primary mb-0" style="font-size:22px;font-weight:600;letter-spacing:-0.3px;">Projects</h4>
      <button class="btn btn-primary" style="border-radius:8px;" @click="openCreate">
        <i class="bi bi-plus-lg mr-1"></i>Create project
      </button>
    </div>

    <!-- Stats -->
    <div class="row mb-4" v-if="stats">
      <div class="col-12 col-md-4 mb-2">
        <div class="bg-white border h-100" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
          <p class="text-secondary mb-1" style="font-size:12px;">All projects</p>
          <p class="text-dark mb-0" style="font-size:26px;font-weight:700;">{{ stats.total_projects }}</p>
          <p class="text-secondary mt-2 mb-0" style="font-size:12px;">
            {{ stats.active_projects }} active &middot; {{ stats.completed_projects }} completed &middot; {{ stats.overdue_projects }} overdue
          </p>
        </div>
      </div>
      <div class="col-12 col-md-4 mb-2">
        <div class="bg-white border h-100" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
          <p class="text-secondary mb-1" style="font-size:12px;">Net profit &amp; loss</p>
          <p class="mb-0" style="font-size:26px;font-weight:700;" :class="stats.net_profit_loss >= 0 ? 'text-dark' : 'text-danger'">
            {{ formatCurrency(stats.net_profit_loss) }}
          </p>
          <p class="text-secondary mt-2 mb-0" style="font-size:12px;">
            Revenue {{ formatCurrency(stats.total_revenue) }} &minus; Expenses {{ formatCurrency(stats.total_expenses) }}
          </p>
        </div>
      </div>
      <div class="col-12 col-md-4 mb-2">
        <div class="bg-white border h-100" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
          <p class="text-secondary mb-1" style="font-size:12px;">Completed</p>
          <p class="text-dark mb-0" style="font-size:26px;font-weight:700;">{{ stats.completed_projects }}</p>
          <p class="text-secondary mt-2 mb-0" style="font-size:12px;">out of {{ stats.total_projects }} projects</p>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="row mb-3">
      <div class="col-md-3 mb-2">
        <input type="text" class="form-control" placeholder="Search by name…" v-model="filters.search" @input="debouncedFetch">
      </div>
      <div class="col-md-3 mb-2">
        <input type="text" class="form-control" placeholder="Filter by client…" v-model="filters.clientSearch" @input="searchFilterCustomers">
        <div v-if="filterCustomerOptions.length" class="list-group mt-1" style="max-height:150px;overflow-y:auto;position:absolute;z-index:5;">
          <a v-for="c in filterCustomerOptions" :key="c.id" href="#" class="list-group-item list-group-item-action py-2" @click.prevent="selectFilterCustomer(c)">{{ c.full_name }}</a>
        </div>
      </div>
      <div class="col-md-3 mb-2">
        <date-range-picker :auto-apply="true" v-model="filterDateRange" :ranges="false" opens="right" :append-to-body="true" class="w-100" style="cursor:pointer;" @update="fetchProjects">
          <template #input="picker">
            <i class="bi bi-calendar3 mr-1"></i>
            <span v-if="picker.startDate">{{ formatDate(picker.startDate) }} – {{ formatDate(picker.endDate) }}</span>
            <span v-else class="text-secondary">Filter by date</span>
          </template>
        </date-range-picker>
      </div>
      <div class="col-md-1 mb-2">
        <input type="number" min="0" class="form-control" placeholder="Min" v-model="filters.min_budget" @change="fetchProjects">
      </div>
      <div class="col-md-1 mb-2">
        <input type="number" min="0" class="form-control" placeholder="Max" v-model="filters.max_budget" @change="fetchProjects">
      </div>
      <div class="col-md-1 mb-2">
        <button class="btn btn-outline-secondary w-100" @click="resetFilters" title="Clear filters"><i class="bi bi-x-lg"></i></button>
      </div>
    </div>

    <div v-if="loading" class="text-center text-secondary py-5">
      <span class="spinner-border spinner-border-sm mr-2"></span>Loading projects…
    </div>

    <div v-else class="bg-white border" style="border-radius:14px;border-color:#e9ecef !important;">
      <div class="table-responsive">
        <table class="table mb-0">
          <thead>
            <tr class="text-secondary" style="font-size:12px;">
              <th class="pl-4">Project</th>
              <th>Client</th>
              <th>Start / End</th>
              <th class="text-right">Budget</th>
              <th>Status</th>
              <th style="width:5%"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="projects.length === 0">
              <td colspan="6" class="text-center text-secondary py-4">No projects yet.</td>
            </tr>
            <tr v-for="p in projects" :key="p.id" class="ov-row-clickable" @click="openDetail(p)">
              <td class="pl-4 text-dark font-weight-bold">{{ p.title }}</td>
              <td class="text-secondary">{{ p.customer ? p.customer.full_name : '—' }}</td>
              <td class="text-secondary">{{ formatDate(p.start_date) }} – {{ formatDate(p.end_date) }}</td>
              <td class="text-right text-dark font-weight-bold">{{ formatCurrency(p.budget) }}</td>
              <td><span class="badge" :class="statusBadgeClass(p.status)">{{ p.status }}</span></td>
              <td @click.stop>
                <b-dropdown variant="link" size="sm" no-caret dropup right toggle-class="text-decoration-none">
                  <template #button-content><i class="bi bi-three-dots-vertical text-secondary"></i></template>
                  <b-dropdown-item @click="openEdit(p)">Edit</b-dropdown-item>
                  <b-dropdown-item :href="`/invoices/create?project_id=${p.id}`">Create invoice</b-dropdown-item>
                  <b-dropdown-item class="text-danger" @click="confirmDelete(p)">Delete</b-dropdown-item>
                </b-dropdown>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Create / Edit modal -->
    <b-modal v-model="showModal" :title="editing ? 'Edit project' : 'New project'" hide-footer no-close-on-backdrop>
      <div v-if="formError" class="alert alert-danger py-2" style="font-size:13px;">{{ formError }}</div>

      <div class="form-group">
        <label>Project title</label>
        <input type="text" class="form-control" v-model="form.title" placeholder="e.g. Website revamp">
      </div>

      <div class="form-group">
        <label>Customer</label>
        <input
          type="text"
          class="form-control"
          v-model="customerSearch"
          placeholder="Search customer by name…"
          @input="searchCustomers"
        >
        <div v-if="selectedCustomerLabel" class="text-secondary mt-1" style="font-size:12px;">Selected: {{ selectedCustomerLabel }}</div>
        <div v-if="customerOptions.length" class="list-group mt-1" style="max-height:150px;overflow-y:auto;">
          <a
            v-for="c in customerOptions"
            :key="c.id"
            href="#"
            class="list-group-item list-group-item-action py-2"
            @click.prevent="selectCustomer(c)"
          >{{ c.full_name }}</a>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group col-md-6">
          <label>Start date</label>
          <date-range-picker :auto-apply="true" :single-date-picker="true" v-model="startDatePicker" class="w-100" />
        </div>
        <div class="form-group col-md-6">
          <label>End date</label>
          <date-range-picker :auto-apply="true" :single-date-picker="true" v-model="endDatePicker" class="w-100" />
        </div>
      </div>

      <div class="form-group">
        <label>Budget (KES)</label>
        <input type="number" min="0" step="0.01" class="form-control" v-model="form.budget">
      </div>

      <div class="form-group">
        <label>Description</label>
        <textarea class="form-control" rows="3" v-model="form.description"></textarea>
      </div>

      <div class="d-flex justify-content-end mt-3" style="gap:8px;">
        <button class="btn btn-light" @click="showModal = false">Cancel</button>
        <button class="btn btn-primary" :disabled="saving" @click="submit">
          <span v-if="saving"><span class="spinner-border spinner-border-sm mr-1"></span>Saving…</span>
          <span v-else>Save</span>
        </button>
      </div>
    </b-modal>

    <!-- Detail modal -->
    <b-modal v-model="showDetail" hide-header hide-footer size="lg" body-class="p-0" content-class="ov-detail-modal">
      <div v-if="detailLoading" class="text-center text-secondary py-5">
        <span class="spinner-border spinner-border-sm mr-2"></span>Loading…
      </div>
      <template v-else-if="detail">
        <div class="ov-detail-header">
          <button class="ov-detail-close" @click="showDetail = false"><i class="bi bi-x-lg"></i></button>
          <span class="badge ov-detail-status" :class="statusBadgeClass(detail.status)">{{ detail.status }}</span>
          <h3 class="mb-1">{{ detail.title }}</h3>
          <p class="mb-0 ov-detail-subtitle">
            <i class="bi bi-person mr-1"></i>{{ detail.customer ? (detail.customer.full_name || detail.customer.company) : '—' }}
            <span class="mx-2">&middot;</span>
            <i class="bi bi-calendar3 mr-1"></i>{{ formatDate(detail.start_date) }} – {{ formatDate(detail.end_date) }}
          </p>
        </div>

        <div class="p-4">
          <div class="ov-progress-wrap mb-4" v-if="detail.end_date">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="text-secondary" style="font-size:12px;">Timeline</span>
              <span class="text-secondary" style="font-size:12px;">
                {{ detail.completion_percentage }}%
                <span v-if="detail.days_remaining >= 0"> &middot; {{ detail.days_remaining }} days left</span>
                <span v-else class="text-danger"> &middot; {{ Math.abs(detail.days_remaining) }} days overdue</span>
              </span>
            </div>
            <div class="ov-progress-track">
              <div
                class="ov-progress-fill"
                :class="detail.status === 'overdue' ? 'ov-progress-fill--danger' : ''"
                :style="{ width: detail.completion_percentage + '%' }"
              ></div>
            </div>
          </div>

          <div class="row no-gutters ov-stat-row mb-4">
            <div class="col-6 col-md-3 ov-stat-tile">
              <p class="ov-stat-label"><i class="bi bi-piggy-bank mr-1"></i>Budget</p>
              <p class="ov-stat-value">{{ formatCurrency(detail.budget) }}</p>
            </div>
            <div class="col-6 col-md-3 ov-stat-tile">
              <p class="ov-stat-label"><i class="bi bi-graph-up-arrow mr-1"></i>Revenue</p>
              <p class="ov-stat-value">{{ formatCurrency(detail.total_revenue) }}</p>
            </div>
            <div class="col-6 col-md-3 ov-stat-tile">
              <p class="ov-stat-label"><i class="bi bi-graph-down-arrow mr-1"></i>Expenses</p>
              <p class="ov-stat-value">{{ formatCurrency(detail.total_expenses) }}</p>
            </div>
            <div class="col-6 col-md-3 ov-stat-tile">
              <p class="ov-stat-label"><i class="bi bi-cash-coin mr-1"></i>Profit / Loss</p>
              <p class="ov-stat-value" :class="detail.profit_loss >= 0 ? '' : 'text-danger'">{{ formatCurrency(detail.profit_loss) }}</p>
            </div>
          </div>

          <div v-if="detail.description" class="mb-3">
            <p class="ov-section-label">About this project</p>
            <p class="text-dark mb-0" style="font-size:13px;">{{ detail.description }}</p>
          </div>
          <div v-if="detail.long_description" class="mb-3">
            <p class="ov-section-label">Details</p>
            <p class="text-dark mb-0" style="font-size:13px;white-space:pre-line;">{{ detail.long_description }}</p>
          </div>

          <div v-if="detail.participants && detail.participants.length" class="mb-4">
            <p class="ov-section-label">Team</p>
            <div class="d-flex flex-wrap" style="gap:8px;">
              <span v-for="participant in detail.participants" :key="participant.id" class="ov-participant-chip">
                <i class="bi bi-person-circle mr-1"></i>{{ participant.user ? participant.user.name : 'Unknown' }}
              </span>
            </div>
          </div>

          <!-- Requisitions -->
          <div class="mb-4">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <p class="ov-section-label mb-0">Requests <span v-if="pendingRequisitionCount" class="badge badge-primary ml-1">{{ pendingRequisitionCount }} pending</span></p>
              <button class="btn btn-outline-secondary btn-sm" style="border-radius:8px;" @click="shareRequisitionLink">
                <i class="bi bi-link-45deg mr-1"></i>{{ linkCopied ? 'Copied!' : 'Share request link' }}
              </button>
            </div>
            <p class="text-secondary mb-2" style="font-size:11px;">Anyone with that link can request materials/spend for this project — no account needed.</p>

            <div v-if="requisitionsLoading" class="text-secondary" style="font-size:12px;"><span class="spinner-border spinner-border-sm mr-1"></span>Loading…</div>
            <div v-else-if="requisitions.length === 0" class="text-secondary" style="font-size:12px;">No requests yet.</div>
            <div v-else class="ov-requisition-list">
              <div v-for="r in requisitions" :key="r.id" class="ov-requisition-row">
                <div class="d-flex align-items-center justify-content-between mb-1">
                  <span class="text-dark font-weight-bold" style="font-size:13px;">{{ r.requester_name }}</span>
                  <span class="badge" :class="requisitionBadgeClass(r.status)">{{ r.status }}</span>
                </div>
                <p class="mb-1 text-secondary" style="font-size:12px;">
                  <span v-for="(item, i) in r.items" :key="i">{{ item.name }} ({{ item.quantity }}{{ item.unit ? ' ' + item.unit : '' }})<span v-if="i < r.items.length - 1">, </span></span>
                </p>
                <p v-if="r.notes" class="mb-2 text-secondary" style="font-size:11px;font-style:italic;">"{{ r.notes }}"</p>
                <p v-if="r.receipt_path" class="mb-2 text-secondary" style="font-size:11px;"><i class="bi bi-paperclip mr-1"></i>Receipt attached</p>
                <div v-if="r.status === 'pending'" class="d-flex" style="gap:8px;">
                  <button class="btn btn-outline-secondary btn-sm" style="border-radius:6px;font-size:11px;" @click="reviewRequisition(r, 'reject')">Reject</button>
                  <button class="btn btn-primary btn-sm" style="border-radius:6px;font-size:11px;" @click="reviewRequisition(r, 'approve')">Approve</button>
                </div>
                <div v-else-if="r.status === 'approved' && !r.expense_id">
                  <button class="btn btn-outline-primary btn-sm" style="border-radius:6px;font-size:11px;" @click="openExpenseForm(r)"><i class="bi bi-receipt mr-1"></i>Record as expense</button>
                </div>
                <p v-else-if="r.expense_id" class="mb-0 text-secondary" style="font-size:11px;"><i class="bi bi-check-circle text-primary mr-1"></i>Recorded as expense</p>
              </div>
            </div>
          </div>

          <!-- Add / record expense -->
          <div v-if="showExpenseForm" class="ov-payment-form mb-4">
            <p class="ov-section-label">{{ expenseFormRequisition ? 'Record as expense' : 'Add expense' }}</p>
            <div class="form-group">
              <label style="font-size:12px;">Category</label>
              <select class="form-control form-control-sm" v-model="expenseForm.category_id">
                <option v-for="c in expenseCategories" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div class="form-group">
              <label style="font-size:12px;">Account <span class="text-muted">(optional)</span></label>
              <select class="form-control form-control-sm" v-model="expenseForm.account_id">
                <option :value="null">Auto (based on category)</option>
                <optgroup v-for="g in chartOfAccounts" :key="g.id" :label="g.name" v-if="g.accounts && g.accounts.length">
                  <option v-for="a in g.accounts" :key="a.id" :value="a.id">{{ a.name }}</option>
                </optgroup>
              </select>
            </div>
            <div class="form-row">
              <div class="form-group col-6">
                <label style="font-size:12px;">Amount</label>
                <input type="number" min="0.01" step="0.01" class="form-control form-control-sm" v-model="expenseForm.amount">
              </div>
              <div class="form-group col-6">
                <label style="font-size:12px;">Funding source</label>
                <select class="form-control form-control-sm" v-model="expenseForm.funding_source">
                  <option value="business_cash">Business cash</option>
                  <option value="payment_method">Payment method</option>
                  <option value="external">External / owner funded</option>
                </select>
              </div>
            </div>
            <div v-if="expenseForm.funding_source === 'payment_method'" class="form-group">
              <label style="font-size:12px;">Payment method</label>
              <select class="form-control form-control-sm" v-model="expenseForm.payment_type_id">
                <option v-for="pt in paymentTypes" :key="pt.id" :value="pt.id">{{ pt.payment_method }}</option>
              </select>
            </div>
            <div class="form-group">
              <label style="font-size:12px;">Notes</label>
              <textarea class="form-control form-control-sm" rows="2" v-model="expenseForm.notes"></textarea>
            </div>
            <div class="form-group">
              <label style="font-size:12px;">Receipt</label>
              <fileuploader description="JPG, PNG, or PDF, 6MB max"></fileuploader>
            </div>
            <div v-if="expenseError" class="text-danger mb-2" style="font-size:12px;">{{ expenseError }}</div>
            <div class="d-flex justify-content-end" style="gap:8px;">
              <button class="btn btn-light btn-sm" @click="showExpenseForm = false">Cancel</button>
              <button class="btn btn-primary btn-sm" :disabled="expenseSaving" @click="submitExpenseForm">
                <span v-if="expenseSaving"><span class="spinner-border spinner-border-sm mr-1"></span>Saving…</span>
                <span v-else>Save expense</span>
              </button>
            </div>
          </div>

          <!-- Receive payment -->
          <div v-if="showPaymentForm" class="ov-payment-form mb-4">
            <p class="ov-section-label">Receive payment</p>
            <div v-if="openInvoices.length === 0" class="text-secondary" style="font-size:12px;">
              This project has no invoice with an outstanding balance yet.
              <a :href="`/invoices/create?project_id=${detail.id}`" class="text-primary">Create one</a> to receive a payment.
            </div>
            <template v-else>
              <div class="form-group">
                <label style="font-size:12px;">Invoice</label>
                <select class="form-control form-control-sm" v-model="paymentForm.invoice_id">
                  <option v-for="inv in openInvoices" :key="inv.id" :value="inv.id">{{ inv.number }} — balance {{ formatCurrency(inv.balance) }}</option>
                </select>
              </div>
              <div class="form-row">
                <div class="form-group col-6">
                  <label style="font-size:12px;">Amount</label>
                  <input type="number" min="0.01" step="0.01" class="form-control form-control-sm" v-model="paymentForm.amount">
                </div>
                <div class="form-group col-6">
                  <label style="font-size:12px;">Payment method</label>
                  <select class="form-control form-control-sm" v-model="paymentForm.payment_type_id">
                    <option v-for="pt in paymentTypes" :key="pt.id" :value="pt.id">{{ pt.payment_method }}</option>
                  </select>
                </div>
              </div>
              <div v-if="paymentError" class="text-danger mb-2" style="font-size:12px;">{{ paymentError }}</div>
              <div class="d-flex justify-content-end" style="gap:8px;">
                <button class="btn btn-light btn-sm" @click="showPaymentForm = false">Cancel</button>
                <button class="btn btn-primary btn-sm" :disabled="recordingPayment" @click="submitPayment">
                  <span v-if="recordingPayment"><span class="spinner-border spinner-border-sm mr-1"></span>Recording…</span>
                  <span v-else>Record payment</span>
                </button>
              </div>
            </template>
          </div>

          <div class="d-flex justify-content-end mt-4" style="gap:8px;">
            <button class="btn btn-outline-secondary btn-sm" style="border-radius:8px;" @click="openExpenseForm(null)">
              <i class="bi bi-receipt mr-1"></i>Add expense
            </button>
            <button class="btn btn-outline-primary btn-sm" style="border-radius:8px;" @click="openPaymentForm">
              <i class="bi bi-cash mr-1"></i>Receive payment
            </button>
            <a :href="`/invoices/create?project_id=${detail.id}`" class="btn btn-outline-secondary btn-sm" style="border-radius:8px;">Create invoice</a>
            <button class="btn btn-primary btn-sm" style="border-radius:8px;" @click="showDetail = false; openEdit(detail);">Edit project</button>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';

export default {
  name: 'ProjectsIndex',

  components: { DateRangePicker },

  data() {
    return {
      loading: true,
      filters: { search: '', clientSearch: '', customer_id: '', min_budget: '', max_budget: '' },
      filterDateRange: { startDate: null, endDate: null },
      filterCustomerOptions: [],
      filterDebounce: null,
      saving: false,
      projects: [],
      stats: null,

      showModal: false,
      editing: null,

      showDetail: false,
      detail: null,
      detailLoading: false,

      requisitions: [],
      requisitionsLoading: false,
      linkCopied: false,

      showPaymentForm: false,
      openInvoices: [],
      paymentTypes: [],
      paymentForm: { invoice_id: '', amount: '', payment_type_id: '' },
      paymentError: '',
      recordingPayment: false,

      showExpenseForm: false,
      expenseFormRequisition: null,
      expenseCategories: [],
      chartOfAccounts: [],
      expenseForm: { category_id: '', account_id: null, amount: '', notes: '', funding_source: 'business_cash', payment_type_id: '' },
      expenseError: '',
      expenseSaving: false,

      formError: '',
      form: { title: '', customer_id: '', budget: '', description: '' },
      startDatePicker: { startDate: null },
      endDatePicker: { startDate: null },

      customerSearch: '',
      customerOptions: [],
      selectedCustomerLabel: '',
      searchDebounce: null,
    };
  },

  computed: {
    pendingRequisitionCount() {
      return this.requisitions.filter(r => r.status === 'pending').length;
    },
  },

  mounted() {
    this.fetchProjects();
    this.fetchStats();
  },

  methods: {
    formatCurrency(v) {
      return 'KES ' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(d) {
      return d ? moment(d).format('DD MMM YYYY') : '—';
    },
    statusBadgeClass(status) {
      return {
        ongoing: 'badge-primary',
        overdue: 'badge-danger',
        completed: 'badge-secondary',
        'yet to start': 'badge-dark',
      }[status] || 'badge-dark';
    },

    async fetchProjects() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/projects', {
          params: {
            search: this.filters.search || undefined,
            customer_id: this.filters.customer_id || undefined,
            min_budget: this.filters.min_budget || undefined,
            max_budget: this.filters.max_budget || undefined,
            start_date: this.filterDateRange.startDate ? moment(this.filterDateRange.startDate).format('YYYY-MM-DD') : undefined,
            end_date: this.filterDateRange.endDate ? moment(this.filterDateRange.endDate).format('YYYY-MM-DD') : undefined,
          },
        });
        this.projects = data;
      } catch (e) {
        this.$toasts.push({ type: 'error', message: 'Could not load projects.' });
      } finally {
        this.loading = false;
      }
    },

    debouncedFetch() {
      clearTimeout(this.filterDebounce);
      this.filterDebounce = setTimeout(() => this.fetchProjects(), 300);
    },

    searchFilterCustomers() {
      clearTimeout(this.filterDebounce);
      this.filterDebounce = setTimeout(async () => {
        if (!this.filters.clientSearch) { this.filterCustomerOptions = []; this.filters.customer_id = ''; this.fetchProjects(); return; }
        try {
          const { data } = await axios.get('/api/allcustomers', { params: { search: this.filters.clientSearch, perPage: 10 } });
          this.filterCustomerOptions = data.data || [];
        } catch (e) {
          this.filterCustomerOptions = [];
        }
      }, 250);
    },

    selectFilterCustomer(customer) {
      this.filters.customer_id = customer.id;
      this.filters.clientSearch = customer.full_name;
      this.filterCustomerOptions = [];
      this.fetchProjects();
    },

    resetFilters() {
      this.filters = { search: '', clientSearch: '', customer_id: '', min_budget: '', max_budget: '' };
      this.filterDateRange = { startDate: null, endDate: null };
      this.filterCustomerOptions = [];
      this.fetchProjects();
    },

    async fetchStats() {
      try {
        const { data } = await axios.get('/api/projects-summary');
        this.stats = data.data;
      } catch (e) {}
    },

    async openDetail(project) {
      this.showDetail = true;
      this.detailLoading = true;
      this.detail = null;
      this.showPaymentForm = false;
      this.linkCopied = false;
      try {
        const { data } = await axios.get(`/api/project/${project.id}/stats`);
        this.detail = data.data;
        this.fetchRequisitions(project.id);
      } catch (e) {
        this.showDetail = false;
        this.$toasts.push({ type: 'error', message: 'Could not load this project.' });
      } finally {
        this.detailLoading = false;
      }
    },

    async fetchRequisitions(projectId) {
      this.requisitionsLoading = true;
      try {
        const { data } = await axios.get(`/api/projects/${projectId}/requisitions`);
        this.requisitions = data;
      } catch (e) {
        this.requisitions = [];
      } finally {
        this.requisitionsLoading = false;
      }
    },

    requisitionBadgeClass(status) {
      return { pending: 'badge-primary', approved: 'badge-secondary', rejected: 'badge-danger' }[status] || 'badge-dark';
    },

    async shareRequisitionLink() {
      try {
        const { data } = await axios.post(`/api/projects/${this.detail.id}/requisition-link`);
        const url = `${window.location.origin}/r/${data.token}`;
        await navigator.clipboard.writeText(url);
        this.linkCopied = true;
        setTimeout(() => { this.linkCopied = false; }, 2000);
      } catch (e) {
        this.$toasts.push({ type: 'error', message: 'Could not generate the link.' });
      }
    },

    async reviewRequisition(requisition, action) {
      try {
        await axios.post(`/api/project-requisitions/${requisition.id}/${action}`);
        this.fetchRequisitions(this.detail.id);
      } catch (e) {
        this.$toasts.push({ type: 'error', message: 'Could not update this request.' });
      }
    },

    async openPaymentForm() {
      this.showPaymentForm = true;
      this.paymentError = '';
      this.paymentForm = { invoice_id: '', amount: '', payment_type_id: '' };
      try {
        const [{ data: invoiceData }, { data: paymentTypeData }] = await Promise.all([
          axios.get('/api/invoices', { params: { project_id: this.detail.id, per_page: 100 } }),
          axios.get('/api/payment-methods'),
        ]);
        this.openInvoices = (invoiceData.data || []).filter(inv => Number(inv.balance) > 0);
        this.paymentTypes = (paymentTypeData.data || []).filter(pt => pt.enabled);
        if (this.openInvoices.length) {
          this.paymentForm.invoice_id = this.openInvoices[0].id;
          this.paymentForm.amount = this.openInvoices[0].balance;
        }
        if (this.paymentTypes.length) {
          this.paymentForm.payment_type_id = this.paymentTypes[0].id;
        }
      } catch (e) {
        this.openInvoices = [];
      }
    },

    async submitPayment() {
      this.paymentError = '';
      if (!this.paymentForm.amount || Number(this.paymentForm.amount) <= 0) {
        this.paymentError = 'Enter an amount greater than zero.';
        return;
      }
      if (!this.paymentForm.payment_type_id) {
        this.paymentError = 'Select a payment method.';
        return;
      }

      this.recordingPayment = true;
      try {
        await axios.post(`/api/invoices/${this.paymentForm.invoice_id}/payments`, {
          amount: this.paymentForm.amount,
          payment_type_id: this.paymentForm.payment_type_id,
        });
        this.showPaymentForm = false;
        this.$toasts.push({ type: 'success', message: 'Payment recorded.' });
        this.openDetail(this.detail);
        this.fetchStats();
      } catch (err) {
        this.paymentError = err.response?.data?.message || 'Could not record this payment.';
      } finally {
        this.recordingPayment = false;
      }
    },

    async openExpenseForm(requisition) {
      this.showExpenseForm = true;
      this.expenseFormRequisition = requisition;
      this.expenseError = '';
      this.expenseForm = {
        category_id: '',
        account_id: null,
        amount: requisition ? '' : '',
        notes: requisition ? requisition.notes : '',
        funding_source: 'business_cash',
        payment_type_id: '',
      };
      try {
        const [{ data: catData }, { data: paymentTypeData }, { data: accountsData }] = await Promise.all([
          axios.get('/api/expense-categories'),
          axios.get('/api/payment-methods'),
          axios.get('/api/accounts-for-selection'),
        ]);
        this.expenseCategories = catData.categories || [];
        this.paymentTypes = (paymentTypeData.data || []).filter(pt => pt.enabled);
        this.chartOfAccounts = Array.isArray(accountsData) ? accountsData : [];
        if (this.expenseCategories.length) this.expenseForm.category_id = this.expenseCategories[0].id;
        if (this.paymentTypes.length) this.expenseForm.payment_type_id = this.paymentTypes[0].id;
      } catch (e) {}
    },

    async submitExpenseForm() {
      this.expenseError = '';
      if (!this.expenseForm.category_id) { this.expenseError = 'Select a category.'; return; }
      if (!this.expenseForm.amount || Number(this.expenseForm.amount) <= 0) { this.expenseError = 'Enter an amount greater than zero.'; return; }
      if (this.expenseForm.funding_source === 'payment_method' && !this.expenseForm.payment_type_id) {
        this.expenseError = 'Select a payment method.';
        return;
      }

      const receiptEl = document.getElementById('filename');
      const receiptPath = receiptEl ? receiptEl.value : '';

      this.expenseSaving = true;
      try {
        if (this.expenseFormRequisition) {
          await axios.post(`/api/project-requisitions/${this.expenseFormRequisition.id}/record-expense`, {
            category_id: this.expenseForm.category_id,
            account_id: this.expenseForm.account_id,
            amount: this.expenseForm.amount,
            notes: this.expenseForm.notes,
            funding_source: this.expenseForm.funding_source,
            payment_type_id: this.expenseForm.payment_type_id || null,
            receipt_path: receiptPath || null,
          });
        } else {
          await axios.post('/api/expense/create', {
            expensetitle: `Project expense — ${this.detail.title}`,
            category_id: this.expenseForm.category_id,
            account_id: this.expenseForm.account_id,
            expenseamount: this.expenseForm.amount,
            notes: this.expenseForm.notes,
            funding_source: this.expenseForm.funding_source,
            payment_type_id: this.expenseForm.payment_type_id || null,
            project_id: this.detail.id,
            receipt_path: receiptPath || null,
          });
        }
        this.showExpenseForm = false;
        this.$toasts.push({ type: 'success', message: 'Expense recorded.' });
        this.openDetail(this.detail);
        this.fetchStats();
      } catch (err) {
        this.expenseError = err.response?.data?.message || 'Could not record this expense.';
      } finally {
        this.expenseSaving = false;
      }
    },

    openCreate() {
      this.editing = null;
      this.formError = '';
      this.form = { title: '', customer_id: '', budget: '', description: '' };
      this.startDatePicker = { startDate: null };
      this.endDatePicker = { startDate: null };
      this.customerSearch = '';
      this.selectedCustomerLabel = '';
      this.customerOptions = [];
      this.showModal = true;
    },

    openEdit(project) {
      this.editing = project;
      this.formError = '';
      this.form = {
        title: project.title,
        customer_id: project.customer_id,
        budget: project.budget,
        description: project.description,
      };
      this.startDatePicker = { startDate: project.start_date ? new Date(project.start_date) : null };
      this.endDatePicker = { startDate: project.end_date ? new Date(project.end_date) : null };
      this.selectedCustomerLabel = project.customer ? project.customer.full_name : '';
      this.customerSearch = '';
      this.customerOptions = [];
      this.showModal = true;
    },

    searchCustomers() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(async () => {
        if (!this.customerSearch) { this.customerOptions = []; return; }
        try {
          const { data } = await axios.get('/api/allcustomers', { params: { search: this.customerSearch, perPage: 10 } });
          this.customerOptions = data.data || [];
        } catch (e) {
          this.customerOptions = [];
        }
      }, 250);
    },

    selectCustomer(customer) {
      this.form.customer_id = customer.id;
      this.selectedCustomerLabel = customer.full_name;
      this.customerOptions = [];
      this.customerSearch = '';
    },

    async submit() {
      this.formError = '';
      if (!this.form.title) { this.formError = 'Enter a project title.'; return; }
      if (!this.editing && !this.form.customer_id) { this.formError = 'Select a customer.'; return; }

      this.saving = true;
      try {
        const payload = {
          title: this.form.title,
          customer_id: this.form.customer_id || undefined,
          budget: this.form.budget || null,
          description: this.form.description || null,
          start_date: this.startDatePicker.startDate ? moment(this.startDatePicker.startDate).format('YYYY-MM-DD') : null,
          end_date: this.endDatePicker.startDate ? moment(this.endDatePicker.startDate).format('YYYY-MM-DD') : null,
        };

        if (this.editing) {
          await axios.put(`/api/projects/${this.editing.id}`, payload);
        } else {
          await axios.post('/api/projects', payload);
        }

        this.showModal = false;
        this.$toasts.push({ type: 'success', message: 'Project saved.' });
        this.fetchProjects();
        this.fetchStats();
      } catch (err) {
        this.formError = err.response?.data?.message || 'Could not save this project.';
      } finally {
        this.saving = false;
      }
    },

    async confirmDelete(project) {
      if (!confirm(`Delete "${project.title}"?`)) return;
      try {
        await axios.delete(`/api/projects/${project.id}`);
        this.$toasts.push({ type: 'success', message: 'Project deleted.' });
        this.fetchProjects();
        this.fetchStats();
      } catch (e) {
        this.$toasts.push({ type: 'error', message: 'Could not delete this project.' });
      }
    },
  },
};
</script>

<style scoped>
.ov-row-clickable {
  cursor: pointer;
}
.ov-row-clickable:hover {
  background: #f8fafc;
}

.ov-detail-header {
  position: relative;
  padding: 28px 32px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e9ecef;
}
.ov-detail-header h3 {
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.3px;
}
.ov-detail-subtitle {
  font-size: 13px;
  color: #64748b;
}
.ov-detail-status {
  position: absolute;
  top: 28px;
  right: 32px;
}
.ov-detail-close {
  position: absolute;
  top: 16px;
  right: 16px;
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 14px;
  padding: 6px;
  line-height: 1;
}
.ov-detail-close:hover {
  color: #0f172a;
}

.ov-progress-track {
  height: 6px;
  border-radius: 3px;
  background: #e9ecef;
  overflow: hidden;
}
.ov-progress-fill {
  height: 100%;
  border-radius: 3px;
  background: #fd4719;
  transition: width 0.3s ease;
}
.ov-progress-fill--danger {
  background: #dc3545;
}

.ov-stat-row {
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
}
.ov-stat-tile {
  padding: 14px 16px;
  border-right: 1px solid #e9ecef;
  border-top: 1px solid #e9ecef;
}
.ov-stat-tile:nth-child(-n+2) {
  border-top: none;
}
@media (min-width: 768px) {
  .ov-stat-tile {
    border-top: none;
  }
  .ov-stat-tile:last-child {
    border-right: none;
  }
}
.ov-stat-label {
  margin: 0 0 4px;
  font-size: 11px;
  color: #64748b;
}
.ov-stat-value {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}

.ov-section-label {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #94a3b8;
}

.ov-participant-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  background: #f1f5f9;
  color: #334155;
  font-size: 12px;
}

.ov-requisition-list {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
}
.ov-requisition-row {
  padding: 12px 14px;
  border-top: 1px solid #e9ecef;
}
.ov-requisition-row:first-child {
  border-top: none;
}
.ov-payment-form {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 16px;
  background: #f8fafc;
}
</style>

<style>
/* Unscoped deliberately — targets a class passed to b-modal's own
   content-class prop, which renders outside this component's scoped
   template subtree. */
.ov-detail-modal {
  border-radius: 16px;
  overflow: hidden;
  border: none;
}
</style>
